import React, { useContext, useEffect, useReducer, useState } from "react";
import classnames from "classnames";

import Modal from "../../../components/modal";
import Selections from "./components/selections";
import TabItem from "./components/tab-item";
import BrowseTab from "./components/browse-tab";
import SearchTab from "./components/search-tab";
import RecentTab from "./components/recent-tab";

import { getOptions, getHierarchy, mapSelectionText, mapSelection } from "./lib/data";
import { reducer, initialState } from "./reducer";

import "./cascading-picklist.scss";
import { SettingsContext } from "../../../contexts/settings";

var BASE_OPTIONS = [];

const CascadingPicklist = (props) => {
    const {
        settings: { cxhTaxonomy },
    } = useContext(SettingsContext);
    const [isBusy, setIsBusy] = useState(false);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [showModal, setShowModal] = useState(false);
    const [tab, setTab] = useState("browse");

    useEffect(() => {
        const fetchInit = async () => {
            const hasValue = props.defaultValue && props.defaultValue.value;
            const response = await getOptions();
            BASE_OPTIONS = response && response.hasOwnProperty("treeRoot") ? response.treeRoot : [];

            if (hasValue) {
                fetchHierarchy(props.defaultValue.value);
            } else {
                dispatch({
                    type: "SET_SELECTION",
                    selected: state.selected,
                    selections: state.selections,
                    options: BASE_OPTIONS,
                });
            }
        };

        fetchInit();
    }, []);

    useEffect(() => {
        if (props.defaultValue !== state.selected) {
            if (!props.defaultValue || !props.defaultValue.value) {
                dispatch({
                    type: "SET_SELECTION",
                    selected: "",
                    selections: [],
                    options: BASE_OPTIONS,
                });
            } else {
                fetchHierarchy(props.defaultValue.value);
            }
        }
    }, [props.defaultValue]);

    const fetchHierarchy = async (cxHId) => {
        setIsBusy(true);
        const selections = await getHierarchy(cxHId);
        setIsBusy(false);

        if (!selections) {
            return;
        }

        const selected = selections.length > 0 ? selections[selections.length - 1] : "";

        dispatch({
            type: "SET_SELECTION",
            options: selected.children,
            selected,
            selections,
        });
    };

    const addTempSelection = async (selected, tempSelections) => {
        if (isBusy) {
            return;
        }

        const newId = selected && selected.cxHId ? selected.cxHId : "";
        const storedId = state.tempSelected && state.tempSelected.cxHId ? state.tempSelected.cxHId : "";

        if (newId === storedId) {
            return;
        }

        setIsBusy(true);
        const options = await getOptions(selected.cxHId);
        setIsBusy(false);

        if (!options) {
            return;
        }

        dispatch({
            type: "SET_TEMP_SELECTION",
            options: options.treeRoot,
            selected,
            selections: tempSelections || [...state.tempSelections, selected],
        });
    };

    const addFullSelection = async (selected) => {
        const selections = await getHierarchy(selected.cxHId);

        if (!selections) {
            return;
        }

        dispatch({
            type: "SET_SELECTION",
            options: [],
            selected,
            selections,
        });
        handleSave(selected);
    };

    const removeAfterIndex = (selected, index) => {
        const tempSelections = state.tempSelections.reduce((arr, opt, i) => {
            if (i <= index) {
                arr.push(opt);
            }

            return arr;
        }, []);

        addTempSelection(selected, tempSelections);
        setTab("browse");
    };

    const clearTaxonomy = async () => {
        dispatch({ type: "CLEAR", options: BASE_OPTIONS });
    };

    const onCancel = async () => {
        closeModal();
        const options = state.selected ? [] : BASE_OPTIONS;
        dispatch({ type: "RESET_WORKING", options });
    };
    const onSave = () => {
        dispatch({ type: "SAVE_TEMP" });
        handleSave(state.tempSelected);
    };

    const handleSave = (selected) => {
        const newId = selected && selected.cxHId ? selected.cxHId : "";
        const storedId = state.selected && state.selected.cxHId ? state.selected.cxHId : "";

        if (newId !== storedId) {
            const selection = mapSelection(selected);
            props.updateOnChange(props.name, selection);
        }

        closeModal();
    };

    const changeTab = (tabId) => {
        setTab(tabId);
    };

    const openModal = () => {
        setShowModal(true);
    };
    const closeModal = () => {
        setShowModal(false);
    };

    const selectClass = classnames("button -primary", { "-disabled": state.workingCanSave });
    const saveButtonAttr = state.options.length > 0 && state.tempSelections.length !== 0 ? { disabled: true } : {};
    const modalButtonText = state.selected ? mapSelectionText(state.selections) : "Add Master Taxonomy";

    return (
        <div className="cascading-picklist-wrapper">
            <button className="button modal-button -small" onClick={openModal} disabled={props.disabled}>
                {modalButtonText}
            </button>

            <Modal show={showModal} onClose={onCancel}>
                <div className="cascading-picklist">
                    <Selections selections={state.tempSelections} clearTaxonomy={clearTaxonomy} removeAfterIndex={removeAfterIndex} />

                    <div className="tabs">
                        <TabItem id="browse" label="Browse" selected={tab} onClick={changeTab} />
                        <TabItem id="search" label="Search" selected={tab} onClick={changeTab} />
                        <TabItem id="recent" label="Recent" selected={tab} onClick={changeTab} />
                    </div>
                    {tab === "browse" && <BrowseTab options={state.options} addTempSelection={addTempSelection} />}
                    {tab === "search" && <SearchTab updateSelected={addFullSelection} />}
                    {tab === "recent" && <RecentTab updateSelected={addFullSelection} />}

                    <div className="button-group">
                        {cxhTaxonomy && cxhTaxonomy.masterReferenceGuideLink && (
                            <a className="hint" href={cxhTaxonomy.masterReferenceGuideLink}>
                                Master Taxonomy Reference Guide
                            </a>
                        )}
                        <button className="button" onClick={onCancel}>
                            Cancel
                        </button>
                        <button className={selectClass} onClick={onSave} {...saveButtonAttr}>
                            Save
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CascadingPicklist;
